import { decodeGrayPng, type GrayImage } from './plate'

/**
 * Decode a plate off the main thread when there is a Worker to do it, and on
 * it when there is not — vitest's jsdom, or a page where the worker failed to
 * start. One worker for every model layer: decoding is one frame at a time per
 * layer and a second thread would buy nothing but memory.
 *
 * The request is *copied* in (a plate is 1-4 MB, a millisecond) rather than
 * transferred, so that if the worker dies the same bytes can still be decoded
 * here instead of leaving the map blank until somebody scrubs.
 */

interface Waiting {
  bytes: ArrayBuffer
  resolve: (image: GrayImage) => void
  reject: (err: Error) => void
}

let worker: Worker | null | undefined
let nextId = 0
const waiting = new Map<number, Waiting>()

function inline(entry: Waiting): void {
  decodeGrayPng(entry.bytes).then(entry.resolve, entry.reject)
}

function start(): Worker | null {
  if (worker !== undefined) return worker
  worker = null
  if (typeof Worker !== 'function') return null
  try {
    const w = new Worker(new URL('./plateWorker.ts', import.meta.url), { type: 'module' })
    w.onmessage = (e: MessageEvent<{ id: number; image?: GrayImage; error?: string }>) => {
      const entry = waiting.get(e.data.id)
      if (!entry) return
      waiting.delete(e.data.id)
      if (e.data.image) entry.resolve(e.data.image)
      else entry.reject(new Error(e.data.error ?? 'plate decode failed'))
    }
    w.onerror = (e) => {
      // The script did not load, or threw at the top level. Stop using it and
      // finish what it was holding here.
      console.warn('plate decode worker failed; decoding on the main thread', e.message)
      w.terminate()
      worker = null
      const held = [...waiting.values()]
      waiting.clear()
      held.forEach(inline)
    }
    worker = w
  } catch (e) {
    console.warn('plate decode worker unavailable; decoding on the main thread', e)
  }
  return worker
}

export function decodePlate(bytes: ArrayBuffer): Promise<GrayImage> {
  return new Promise((resolve, reject) => {
    const entry = { bytes, resolve, reject }
    const w = start()
    if (!w) {
      inline(entry)
      return
    }
    const id = ++nextId
    waiting.set(id, entry)
    w.postMessage({ id, bytes })
  })
}
